import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import StripeCheckout from 'react-stripe-checkout';
import { useSelector } from 'react-redux';
import { SkeletonLoading } from '@saleshandy/design-system';
import type { IProps } from './billing-summary-modal-container';
import Modal from '../../../../../../../../shared/design-system/components/atoms/modal';
import Button from '../../../../../../../../shared/design-system/components/atoms/button';
import { accessibleOnClick } from '../../../../../../../../shared/utils/accessible-on-click';
import Input from '../../../../../../../../shared/design-system/components/input/input';
import { validate } from '../../../../../../../../shared/design-system/components/molecules/upgrade-plan-billing-section/validator';
import { isRequestPending } from '../../../../utils/helper';
import { Images } from '../../../../../../../../shared/app-constants';
import { getSubtotalDiscountAmount } from '../../../../../../../../shared/design-system/components/molecules/upgrade-plan-billing-section/helper/helper';
import { executeOnRequestStatus } from '../../../../../../../../shared/utils/execute-on-request-status';
import { supportUrls } from '../../../../../../../../shared/utils/urls';
import {
  formatDiscountAmount,
  getTotalAmountForOutreachPlan,
} from './utils/helper';
import ImageIcon from '../../../../../../../../shared/components/images/image-icon';
import { AnalyticsEvents } from '../../../../../../../../shared/enums/analytics';
import { getUserTrackingId } from '../../../../../../../../shared/utils/user-details';
import { RootState } from '../../../../../../../../store/root-reducer';
import {
  computeLeadRevealCredits,
  formatLeadFinderCredits,
} from '../../../../../../../v2-lead/helpers/helper';
import { SubscriptionPlans } from '../../../../../../../../shared/utils/subscription-plans';

const BillingSummaryModal: React.FC<IProps> = ({
  show,
  onClose,
  selectedPlanDetails,
  couponCodeDiscount,
  couponCodeDiscountType,
  onPurchase,
  //* Request Response State
  sendApplyCouponCodeRequestMessage,
  isSendApplyCouponCodeRequestError,
  sendApplyCouponCodeRequestStatus,
  sendPurchaseLeadFinderSubscriptionRequestStatus,
  sendPurchaseLeadFinderModifySubscriptionRequestStatus,
  sendGetCalculatePayResponse,
  sendGetCalculatePayRequestStatus,
  //* API Request
  sendApplyCouponCodeRequest,
  //* Redux Util Function
  deleteAppliedCouponCode,
  currentPlanName,
  isPaddleFreeUser,
  onPaddlePayNow,
  paddleBillingData,
}) => {
  const { t } = useTranslation();

  const leadReveal = useSelector(
    (state: RootState) =>
      state?.home?.getPostLoadMetaResponse?.accountUsageQuotaRemaining?.[
        'LEAD.REVEAL'
      ],
  );
  const leadRevealBucket1 = useSelector(
    (state: RootState) =>
      state?.home?.getPostLoadMetaResponse?.accountUsageQuotaRemaining?.[
        'LEAD.REVEAL.BUCKET1'
      ],
  );
  const leadRevealBucket2 = useSelector(
    (state: RootState) =>
      state?.home?.getPostLoadMetaResponse?.accountUsageQuotaRemaining?.[
        'LEAD.REVEAL.BUCKET2'
      ],
  );
  const isBucket1Frozen = useSelector(
    (state: RootState) => state?.home?.leadFinder?.isBucket1Frozen,
  );

  const currentLeadFinderPlanCode = useSelector(
    (state: RootState) => state?.home?.leadFinderSubscription?.planCode,
  );
  const isCurrentLfPlan = currentLeadFinderPlanCode?.startsWith('lf');
  const isLeadFinderFreeOrTrial =
    currentLeadFinderPlanCode === SubscriptionPlans.LEAD_FINDER_FREE ||
    currentLeadFinderPlanCode === SubscriptionPlans.LEAD_FINDER_TRIAL;

  const existingCredits = computeLeadRevealCredits(
    leadReveal,
    leadRevealBucket1,
    leadRevealBucket2,
    { isBucket1Frozen },
  );

  const totalAmount = selectedPlanDetails?.amount;
  const planId = Number(selectedPlanDetails?.id);
  const planName = selectedPlanDetails?.planName;
  const isApplyCouponCodeInProgress = isRequestPending(
    sendApplyCouponCodeRequestStatus,
  );
  const isPurchaseInProgress =
    isRequestPending(sendPurchaseLeadFinderSubscriptionRequestStatus) ||
    isRequestPending(sendPurchaseLeadFinderModifySubscriptionRequestStatus);

  const totalAmountToPay: number = getTotalAmountForOutreachPlan({
    totalAmount: Number(sendGetCalculatePayResponse?.basePlanAmount),
    discountType: couponCodeDiscountType,
    discount: couponCodeDiscount,
    sendGetCalculatePayResponse,
  });

  const [havePromoCode, setHavePromoCode] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [error, setError] = useState<string>('');

  const userHasPromoCode = (value) => {
    setHavePromoCode(value);
  };

  const onCouponCodeChange = (value) => {
    setCouponCode(value);
  };

  const handleDeleteAppliedCouponCode = () => {
    onCouponCodeChange('');
    deleteAppliedCouponCode();
    userHasPromoCode(false);
  };

  const onApplyCouponCode = (e) => {
    e.preventDefault();

    let isError = false;

    const checkError = validate('couponCode', couponCode);
    isError = isError || !!checkError;
    setError(checkError);

    if (isError) {
      return;
    }

    sendApplyCouponCodeRequest({
      couponCode,
      amount: Number(totalAmountToPay),
      planId,
    });
  };

  const onPurchaseHandler = (token) => {
    onPurchase(token, couponCode);
  };

  const handleModalClose = () => {
    handleDeleteAppliedCouponCode();
    onClose();
  };

  const handlePayNowAnalyticsEvent = () => {
    window.analytics?.track?.({
      userId: getUserTrackingId(),
      event: AnalyticsEvents.ClickedOnPayNow,
      properties: {
        Source: 'Subscription',
        current_plan: currentPlanName,
        'Clicked Plan': planName,
      },
    });
  };

  //* useEffect
  // Purchase subscription
  useEffect(() => {
    executeOnRequestStatus({
      status: sendPurchaseLeadFinderSubscriptionRequestStatus,
      onFailed: () => {
        handleDeleteAppliedCouponCode();
      },
    });
  }, [sendPurchaseLeadFinderSubscriptionRequestStatus]);

  // Modify subscription
  useEffect(() => {
    executeOnRequestStatus({
      status: sendPurchaseLeadFinderModifySubscriptionRequestStatus,
      onFailed: () => {
        handleDeleteAppliedCouponCode();
      },
    });
  }, [sendPurchaseLeadFinderModifySubscriptionRequestStatus]);

  const renderCouponCodeView = () => (
    <div className="promo-code-container">
      {havePromoCode && (
        <div className="coupon-code-input-wrapper mt-3">
          <Input
            name="couponCode"
            placeholder="Enter Coupon"
            value={couponCode}
            variant={
              ((error || isSendApplyCouponCodeRequestError) &&
                Input.Variant.Error) ||
              (couponCodeDiscount && Input.Variant.Succeeded)
            }
            caption={error || sendApplyCouponCodeRequestMessage}
            onChange={onCouponCodeChange}
            className="coupon-code-input"
            button={
              !isApplyCouponCodeInProgress &&
              !couponCodeDiscount && {
                buttonTitle: 'Apply',
                onClick: (e) => onApplyCouponCode(e),
              }
            }
            icons={[
              isApplyCouponCodeInProgress && {
                place: Input.IconPlace.Right,
                identifier: 'spinner-alt',
                colorDefault: true,
                className: 'icon-loading',
              },
              ...(couponCodeDiscount
                ? [
                    {
                      place: Input.IconPlace.Right,
                      identifier: 'trash',
                      colorDefault: true,
                      className: 'icon-1 icon-2',
                      onClick: () => handleDeleteAppliedCouponCode(),
                    },
                  ]
                : []),
            ]}
          />
        </div>
      )}

      {couponCodeDiscount && (
        <div className="d-flex justify-content-between subtotal-discount-amount mt-4">
          <span className="semibold-1 popover-arrow-color-txt">
            {t('labels.discount')}
          </span>
          <span className="semibold-2 popover-arrow-color-txt">{`$${formatDiscountAmount(
            getSubtotalDiscountAmount(
              couponCodeDiscountType,
              Number(sendGetCalculatePayResponse?.basePlanAmount),
              couponCodeDiscount,
            ),
          )}`}</span>
        </div>
      )}
    </div>
  );

  const renderStripeButton = () => (
    <StripeCheckout
      token={onPurchaseHandler}
      name="Saleshandy"
      image={Images.SHIcon70}
      stripeKey={process.env.REACT_APP_CFG_PAY_01}
      amount={totalAmountToPay * 100}
      zipCode
      billingAddress
      ComponentClass="div"
    >
      <Button
        onClick={() => handlePayNowAnalyticsEvent()}
        variant={Button.Variant.Primary}
        isLoading={isPurchaseInProgress}
        disabled={isPurchaseInProgress}
        className="solid-primary-new w-100"
      >
        {t('labels.pay_now')}
      </Button>
    </StripeCheckout>
  );

  const renderPaddleButton = () => (
    <Button
      onClick={() => {
        handlePayNowAnalyticsEvent();
        onPaddlePayNow?.();
      }}
      variant={Button.Variant.Primary}
      className="solid-primary-new w-100"
    >
      {t('labels.pay_now')}
    </Button>
  );

  return (
    <Modal
      show={show}
      className="billing-summary-modal"
      showCloseIcon={true}
      onClose={handleModalClose}
      hideFooter={true}
      backdrop="static"
      titleContent="Billing Summary"
    >
      {(() => {
        // Wait for calculate-pay response before showing content
        if (!isPaddleFreeUser && !sendGetCalculatePayResponse) {
          return (
            <>
              {/* Plan Name */}
              <div className="d-flex justify-content-between align-items-center">
                <SkeletonLoading height={14} width={80} />
                <SkeletonLoading height={14} width={120} />
              </div>
              {/* Plan Amount */}
              <div className="d-flex justify-content-between align-items-center mt-2">
                <SkeletonLoading height={14} width={100} />
                <SkeletonLoading height={14} width={85} />
              </div>
              {/* Add-on Credits */}
              <div className="d-flex justify-content-between align-items-center mt-2">
                <SkeletonLoading height={14} width={150} />
                <SkeletonLoading height={14} width={75} />
              </div>
              {/* Total Amount + divider */}
              <div className="divider-bottom">
                <div className="d-flex justify-content-between align-items-center mt-2">
                  <SkeletonLoading height={14} width={100} />
                  <SkeletonLoading height={14} width={90} />
                </div>
                <div className="d-flex justify-content-end mt-1">
                  <SkeletonLoading height={12} width={120} />
                </div>
              </div>
              {/* Total Amount to pay today */}
              <div className="d-flex justify-content-between align-items-center">
                <SkeletonLoading height={16} width={170} />
                <SkeletonLoading height={16} width={90} />
              </div>
              {/* Next billing cycle */}
              <div className="mt-2">
                <SkeletonLoading height={50} width="100%" />
              </div>
              {/* Refund text */}
              <div className="mt-2 d-flex justify-content-center">
                <SkeletonLoading height={28} width="90%" />
              </div>
              {/* Important note */}
              <div className="mt-2">
                <SkeletonLoading height={90} width="100%" />
              </div>
              {/* Pay Now button */}
              <div className="mt-3">
                <SkeletonLoading height={40} width="100%" />
              </div>
            </>
          );
        }

        const calcResp = isPaddleFreeUser
          ? paddleBillingData
          : sendGetCalculatePayResponse ?? null;
        const isNewPlanResponse = calcResp != null;
        const isYearly = isNewPlanResponse
          ? calcResp?.billingCycle === 365
          : false;
        const periodSuffix = isYearly ? '/year' : '/month';

        // Extract base tier (Starter/Pro) and append cycle
        const displayPlanName = (() => {
          const name = calcResp?.planName || planName || '';
          const tierMatch = name.match(/\b(Starter|Pro)\b/i);
          const tier = tierMatch
            ? tierMatch[1]
            : name.replace(/^Lead Finder\s*/i, '');
          const cycleName = isYearly ? 'Yearly' : 'Monthly';
          return `${tier} (${cycleName})`;
        })();

        // Check if base plan is same as current (already paid)
        const isBasePlanAlreadyPaid =
          isNewPlanResponse &&
          calcResp?.currentPlanAmount !== undefined &&
          calcResp?.basePlanAmount !== undefined &&
          calcResp.currentPlanAmount === calcResp.basePlanAmount;

        if (isNewPlanResponse) {
          return (
            <>
              <div className="d-flex justify-content-between align-items-center">
                <span className="regular-1 gray-txt-12">Plan Name</span>
                <span className="regular-2 font-medium popover-arrow-color-txt">
                  {displayPlanName}
                  {isBasePlanAlreadyPaid && (
                    <span className="regular-1 gray-txt-12"> (Current)</span>
                  )}
                </span>
              </div>

              <div className="d-flex justify-content-between align-items-center mt-2">
                <span className="regular-1 gray-txt-12">
                  Plan Amount
                  {isBasePlanAlreadyPaid && (
                    <span className="regular-1 gray-txt-12">
                      {' '}
                      (already paid)
                    </span>
                  )}
                </span>
                <span
                  className={`semibold-2 ${
                    isBasePlanAlreadyPaid
                      ? 'gray-txt-12 text-decoration-line-through'
                      : 'popover-arrow-color-txt'
                  }`}
                >
                  ${calcResp.basePlanAmount?.toFixed(2)}
                  {periodSuffix}
                </span>
              </div>

              {calcResp.addonAmount != null && calcResp.addonCredits != null && (
                <div className="d-flex justify-content-between align-items-center mt-2">
                  <span className="regular-1 gray-txt-12">
                    Add-on Credits (
                    {calcResp.addonName ||
                      `${calcResp.addonCredits.toLocaleString()}`}
                    {periodSuffix})
                  </span>
                  <span className="semibold-2 popover-arrow-color-txt">
                    ${calcResp.addonAmount}
                    {periodSuffix}
                  </span>
                </div>
              )}

              <div className="divider-bottom">
                <div className="d-flex justify-content-between align-items-center mt-2">
                  <span className="regular-1 gray-txt-12">Total Amount</span>
                  <span className="semibold-2 popover-arrow-color-txt">
                    ${calcResp.planAmount?.toFixed(2)}
                  </span>
                </div>
                {!isPaddleFreeUser && (
                  <>
                    <div className="d-flex flex-column align-items-end">
                      {!havePromoCode && (
                        <span
                          role="button"
                          className="font-10 font-medium blue-txt-11 mt-1 pointer"
                          {...accessibleOnClick(userHasPromoCode)}
                        >
                          Have a discount code?
                        </span>
                      )}
                    </div>
                    {renderCouponCodeView()}
                  </>
                )}
              </div>

              <div className="d-flex justify-content-between align-items-center">
                <span className="semibold-1 popover-arrow-color-txt">
                  Total Amount to pay today
                </span>
                <span className="semibold-2 popover-arrow-color-txt">
                  {isRequestPending(sendGetCalculatePayRequestStatus) ? (
                    <div className="loader-container d-flex justify-content-center align-items-center">
                      <ImageIcon src={Images.Loader} />
                    </div>
                  ) : (
                    `$${
                      isPaddleFreeUser
                        ? (paddleBillingData?.planAmount ?? 0).toFixed(2)
                        : totalAmountToPay
                    }`
                  )}
                </span>
              </div>

              {calcResp.nextBillingAmount != null && (
                <div className="billing-summary-next-cycle mt-2">
                  <div className="semibold-1 font-10">
                    Next billing cycle:{' '}
                    <span className="regular-1">
                      ${calcResp.nextBillingAmount?.toFixed(2)}
                      {periodSuffix}
                    </span>
                  </div>
                  <div className="regular-1 font-10 blue-txt-11">
                    50% of unused credits will rollover to the next{' '}
                    {isYearly ? 'year' : 'month'}.
                  </div>
                </div>
              )}
            </>
          );
        }

        // Fallback: original layout for old plans
        return (
          <>
            <div className="d-flex justify-content-between align-items-center">
              <span className="regular-1 gray-txt-12">Plan Name</span>
              <span className="regular-2 font-medium popover-arrow-color-txt">
                {planName}
              </span>
            </div>

            <div className="divider-bottom">
              <div className="d-flex justify-content-between align-items-center">
                <span className="regular-1 gray-txt-12">Total Amount</span>
                <span className="semibold-2 popover-arrow-color-txt">
                  ${totalAmount}
                </span>
              </div>
              <div className="d-flex flex-column align-items-end">
                {!havePromoCode && (
                  <span
                    role="button"
                    className="font-10 font-medium blue-txt-11 mt-1 pointer"
                    {...accessibleOnClick(userHasPromoCode)}
                  >
                    Have a discount code?
                  </span>
                )}
              </div>
              {renderCouponCodeView()}
            </div>

            <div className="d-flex justify-content-between align-items-center">
              <span className="semibold-1 popover-arrow-color-txt">
                Total Amount to pay today
              </span>
              <span className="semibold-2 popover-arrow-color-txt">
                {isRequestPending(sendGetCalculatePayRequestStatus) ? (
                  <div className="loader-container d-flex justify-content-center align-items-center">
                    <ImageIcon src={Images.Loader} />
                  </div>
                ) : (
                  `$${
                    isPaddleFreeUser
                      ? (paddleBillingData?.planAmount ?? 0).toFixed(2)
                      : totalAmountToPay
                  }`
                )}
              </span>
            </div>
          </>
        );
      })()}

      <div className="mt-2">
        <p className="regular-1 font-10 gray-txt-15 text-center">
          Your subscription will auto-renew until you cancel. By upgrading, you
          agree to our{' '}
          <a
            href={supportUrls.refundPolicy}
            target="_blank"
            className="link"
            rel="noreferrer"
          >
            refund policy.
          </a>
        </p>
      </div>

      {(sendGetCalculatePayResponse != null || isPaddleFreeUser) &&
        !isCurrentLfPlan &&
        !isLeadFinderFreeOrTrial && (
          <div className="billing-summary-important-note mt-2 mb-3">
            <span className="semibold-1 font-10 popover-arrow-color-txt">
              Important note:
            </span>
            <ul className="billing-summary-important-note__list">
              <li>
                The old version of Lead Finder will no longer be accessible for
                search and reveal functionalities.
              </li>
              <li>
                You will still be able to access your previously saved leads.
              </li>
              <li>
                Your{' '}
                <span className="font-weight-bold">
                  {formatLeadFinderCredits(existingCredits.availableCredits)}{' '}
                  existing credits
                </span>{' '}
                will carry over to your new plan — no credits lost.
              </li>
            </ul>
          </div>
        )}

      {isPaddleFreeUser ? renderPaddleButton() : renderStripeButton()}
    </Modal>
  );
};

export default BillingSummaryModal;
